import { useState, useEffect, useRef } from "react";
import { API_URL, DEMO_VIDEO_URL } from "../config/env";

export default function HamburgerMenu() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuTab, setMenuTab] = useState("about");

  // Allow other components to open the menu to a specific tab via custom event
  useEffect(() => {
    const handler = (e) => {
      const tab = e?.detail?.tab;
      if (tab) setMenuTab(tab);
      setMenuOpen(true);
    };
    window.addEventListener("pitcherly-open-menu", handler);
    return () => window.removeEventListener("pitcherly-open-menu", handler);
  }, []);

  const closeMenuAndFocusEmail = () => {
    setMenuOpen(false);
    setTimeout(() => {
      const el = document.querySelector('.auth-card input[type="email"]');
      if (el) { el.focus(); el.scrollIntoView({ behavior: "smooth", block: "center" }); }
    }, 250);
  };

  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactMsg, setContactMsg] = useState("");
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const menuPanelRef = useRef(null);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    if (menuOpen) document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [menuOpen]);

  const handleContact = async (e) => {
    e.preventDefault();
    setContactSending(true);
    try {
      await fetch(`${API_URL}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: contactName, email: contactEmail, message: contactMsg }),
      });
      setContactSent(true);
      setContactName(""); setContactEmail(""); setContactMsg("");
    } catch (e) { console.warn("Contact form submit failed:", e); }
    setContactSending(false);
  };

  return (
    <>
      <button className="hamburger-btn" onClick={() => { setMenuOpen(true); setContactSent(false); }} aria-label="Menu">&#9776;</button>
      <div className={`hmenu-overlay ${menuOpen ? "open" : ""}`} onClick={() => setMenuOpen(false)} />
      <div className={`hmenu-panel ${menuOpen ? "open" : ""}`} ref={menuPanelRef}>
        <div className="hmenu-header">
          <div className="hmenu-logo">Pitcher<em>ly</em></div>
          <button className="hmenu-close" onClick={() => setMenuOpen(false)}>&times;</button>
        </div>
        <div className="hmenu-tabs">
          {[
            { id: "why", label: "Why Pitcherly", mobileOnly: true },
            { id: "about", label: "About" },
            { id: "demo", label: "Demo" },
            { id: "features", label: "Features" },
            { id: "contact", label: "Contact" },
          ].map(t => (
            <button
              key={t.id}
              className={`hmenu-tab ${menuTab === t.id ? "active" : ""}${t.mobileOnly ? " mobile-only-tab" : ""}`}
              onClick={() => setMenuTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="hmenu-body">

          {menuTab === "why" && (
            <>
              <div className="hmenu-heading">The AI writing toolkit built for real estate agents</div>
              <div className="hmenu-text">Save 5-10 hours a week. Pays for itself with one extra deal.</div>

              <div className="why-features">
                {[
                  { icon: "🏠", title: "MLS Listing Generator", desc: "professional copy in seconds" },
                  { icon: "📧", title: "Lead Follow-Up Emails", desc: "3 personalized emails per lead" },
                  { icon: "📋", title: "Contract Summarizer", desc: "Breaks down contracts into clear, simple language" },
                  { icon: "🏡", title: "Open House Follow-Up", desc: "cycle through every visitor fast" },
                  { icon: "📊", title: "Seller Update Emails", desc: "weekly updates that retain clients" },
                  { icon: "👤", title: "Agent Bio Generator", desc: "full and short bio in one click" },
                  { icon: "📈", title: "CMA Generator", desc: "win more listing presentations" },
                  { icon: "🏠", title: "Property Info", desc: "look up any property and save details instantly" },
                  { icon: "👥", title: "Contacts & CRM", desc: "manage leads, import CSV, track every client" },
                  { icon: "📤", title: "Email Export", desc: "send directly via Gmail, Outlook, or any email client" },
                ].map(f => (
                  <div className="why-feature" key={f.title}>
                    <span className="why-feature-icon">{f.icon}</span>
                    <div>
                      <div className="why-feature-title">{f.title}</div>
                      <div className="why-feature-desc">{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              {DEMO_VIDEO_URL && (
                <div className="why-demo">
                  <div className="why-section-label">See it in action</div>
                  <div className="auth-demo-player">
                    <iframe
                      src={DEMO_VIDEO_URL}
                      title="Pitcherly Demo"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
              )}

              <div className="why-section-label">Pricing</div>
              <div className="why-pricing">
                <div className="why-price-card" style={{opacity:0.92,borderColor:"rgba(255,255,255,0.06)"}}>
                  <div className="why-price-name">Lite</div>
                  <div className="why-price-amount">$19<span>/month</span></div>
                  <ul className="why-price-list">
                    <li>50 generations / month</li>
                    <li>All 10 writing tools</li>
                    <li>No property lookups</li>
                  </ul>
                  <button className="why-price-cta starter" onClick={closeMenuAndFocusEmail}>Start Free Trial →</button>
                </div>
                <div className="why-price-card">
                  <div className="why-price-name">Starter</div>
                  <div className="why-price-amount">$49<span>/month</span></div>
                  <ul className="why-price-list">
                    <li>200 generations / month</li>
                    <li>All 10 writing tools</li>
                    <li>10 verified property lookups</li>
                    <li>Market-data CMAs (live estimate + comps)</li>
                    <li>Contacts & CRM</li>
                  </ul>
                  <button className="why-price-cta starter" onClick={closeMenuAndFocusEmail}>Start Free Trial →</button>
                </div>
                <div className="why-price-card featured">
                  <div className="why-price-badge">Most Popular</div>
                  <div className="why-price-name">Pro</div>
                  <div className="why-price-amount">$99<span>/month</span></div>
                  <ul className="why-price-list">
                    <li>Unlimited generations</li>
                    <li>All 10 writing tools</li>
                    <li>50 verified property lookups</li>
                    <li>Market-data CMAs (live estimate + comps)</li>
                    <li>Priority support</li>
                    <li>Early access to new features</li>
                  </ul>
                  <button className="why-price-cta pro" onClick={closeMenuAndFocusEmail}>Start Free Trial →</button>
                </div>
              </div>
              <div className="why-fine">7-day free trial · Cancel anytime · No commitment</div>
            </>
          )}

          {menuTab === "about" && (
            <>
              <div className="hmenu-heading">Built for Real Estate Agents</div>
              <div className="hmenu-text">
                Pitcherly is an AI writing tool built specifically for real estate agents. We handle the copy so you can focus on closing. From MLS listing descriptions to follow-up emails, open house copy, contract summaries, agent bios, CMAs, and more — Pitcherly writes it all in seconds.
              </div>
              <div className="hmenu-text">
                Whether you're a solo agent or part of a large brokerage, Pitcherly saves you 5-10 hours every week on writing tasks. That's time you can spend showing homes, building client relationships, and closing more deals.
              </div>
              <div className="hmenu-text">
                We built Pitcherly because we saw agents spending hours on copy that AI could handle in seconds. Every tool is designed specifically for real estate — not generic AI, but purpose-built writing tools that understand your industry.
              </div>
              <div className="hmenu-founded">Founded in 2026 &middot; Brea, California</div>
              <div className="hmenu-stats">
                <div className="hmenu-stat">
                  <div className="hmenu-stat-val">10</div>
                  <div className="hmenu-stat-label">AI Tools</div>
                </div>
                <div className="hmenu-stat">
                  <div className="hmenu-stat-val">Seconds</div>
                  <div className="hmenu-stat-label">Not Hours</div>
                </div>
                <div className="hmenu-stat">
                  <div className="hmenu-stat-val">99%</div>
                  <div className="hmenu-stat-label">Less Writing</div>
                </div>
              </div>
              <div style={{marginTop:24,fontSize:12,color:"var(--text3)"}}>
                <a href="/terms" style={{color:"var(--text3)",textDecoration:"none",marginRight:12}}>Terms of Service</a>
                <a href="/privacy" style={{color:"var(--text3)",textDecoration:"none"}}>Privacy Policy</a>
              </div>
            </>
          )}

          {menuTab === "contact" && (
            <>
              <div className="hmenu-heading">We're here to help</div>
              <div className="hmenu-contact-info">
                <a href="mailto:support@pitcherly.com">support@pitcherly.com</a>
                <div className="hmenu-response">We respond within 24 hours on business days</div>
              </div>
              {contactSent ? (
                <div className="hmenu-form-success">Message sent! We'll get back to you within 24 hours.</div>
              ) : (
                <form className="hmenu-form" onSubmit={handleContact}>
                  <input placeholder="Your name" value={contactName} onChange={e => setContactName(e.target.value)} required />
                  <input type="email" placeholder="Your email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} required />
                  <textarea placeholder="How can we help?" value={contactMsg} onChange={e => setContactMsg(e.target.value)} required />
                  <button className="hmenu-form-btn" type="submit" disabled={contactSending}>
                    {contactSending ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}
            </>
          )}

          {menuTab === "demo" && (
            <>
              <div className="hmenu-heading">See Pitcherly in action</div>
              <div className="hmenu-subtitle">Watch how agents write a full MLS listing in under 60 seconds</div>
              {DEMO_VIDEO_URL ? (
                <div className="auth-demo-player" style={{marginBottom: 20}}>
                  <iframe
                    src={DEMO_VIDEO_URL}
                    title="Pitcherly Demo"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <div className="hmenu-placeholder">Demo video coming soon</div>
              )}
              <ul className="hmenu-bullets">
                <li><span>&#10003;</span> Full MLS listing generated in seconds</li>
                <li><span>&#10003;</span> Follow-up emails written automatically</li>
                <li><span>&#10003;</span> Copy and paste directly into your MLS</li>
              </ul>
              <button className="hmenu-cta" onClick={() => { setMenuOpen(false); }}>Start Free Trial &rarr;</button>
            </>
          )}

          {menuTab === "features" && (
            <>
              <div className="hmenu-heading">Everything you need to write faster</div>
              <div className="hmenu-features-grid">
                {[
                  { icon: "🏠", name: "MLS Listing", desc: "Full listing descriptions with neighborhood copy" },
                  { icon: "📧", name: "Follow-Up Emails", desc: "3-email sequences for any lead stage" },
                  { icon: "🏡", name: "Open House", desc: "Follow-ups and Just Sold announcements" },
                  { icon: "📊", name: "CMA", desc: "Comparative market analysis emails" },
                  { icon: "📋", name: "Contract Summary", desc: "Clear, easy-to-read contract summaries" },
                  { icon: "👤", name: "Agent Bio", desc: "Professional bios in any tone" },
                  { icon: "📉", name: "Seller Updates", desc: "Weekly market updates for sellers" },
                  { icon: "📤", name: "FSBO Outreach", desc: "For Sale By Owner prospecting emails" },
                  { icon: "📋", name: "Expired Outreach", desc: "Expired listing prospecting emails" },
                  { icon: "🏠", name: "Property Info", desc: "Look up any property and save details" },
                  { icon: "👥", name: "Contacts & CRM", desc: "Manage leads, import CSV, track clients" },
                  { icon: "📤", name: "Email Export", desc: "Send via Gmail, Outlook, or any client" },
                ].map(f => (
                  <div className="hmenu-feature-card" key={f.name}>
                    <div className="hmenu-feature-icon">{f.icon}</div>
                    <div className="hmenu-feature-name">{f.name}</div>
                    <div className="hmenu-feature-desc">{f.desc}</div>
                  </div>
                ))}
              </div>
              <div className="hmenu-compare-wrap">
              <table className="hmenu-compare">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Free Trial</th>
                    <th>Lite $19/mo</th>
                    <th>Starter $49/mo</th>
                    <th className="hmenu-pro-col">Pro $99/mo</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["AI Writing Tools (all 10)", true, true, true, true],
                    ["MLS Listing Generator", true, true, true, true],
                    ["Follow-Up Email Sequences", true, true, true, true],
                    ["Contract Summarizer", true, true, true, true],
                    ["Agent Bio Generator", true, true, true, true],
                    ["CMA Reports", true, true, true, true],
                    ["Contacts & CRM", true, true, true, true],
                    ["CSV Contact Import", true, true, true, true],
                    ["Email Export (Gmail/Outlook)", true, true, true, true],
                    ["Generations", "50 lifetime", "50/month", "200/month", "Unlimited"],
                    ["Verified Property Lookups (RentCast)", false, false, "10/month", "50/month"],
                    ["Market-Data CMAs (live estimate + comps)", false, false, true, true],
                    ["Save Properties to Library", true, true, true, true],
                  ].map(([feat, trial, lite, starter, pro], i) => (
                    <tr key={i}>
                      <td>{feat}</td>
                      <td>{trial === true ? <span className="hmenu-check">&#10003;</span> : trial === false ? <span className="hmenu-x">&#10007;</span> : trial}</td>
                      <td>{lite === true ? <span className="hmenu-check">&#10003;</span> : lite === false ? <span className="hmenu-x">&#10007;</span> : lite}</td>
                      <td>{starter === true ? <span className="hmenu-check">&#10003;</span> : starter === false ? <span className="hmenu-x">&#10007;</span> : starter}</td>
                      <td className="hmenu-pro-col">{pro === true ? <span className="hmenu-check">&#10003;</span> : pro === false ? <span className="hmenu-x">&#10007;</span> : <span style={{color:'var(--accent)',fontWeight:600}}>{pro}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}

        </div>
      </div>
    </>
  );
}
